import type { CustomField } from '@sales-center/common'

import { customFieldTypeToFormType } from '@user/components/CustomFields/Editable/mapper'
import type { IAutoReviewMissingField } from '@user/store/draftLeads/typings'

import { CONTACT_GROUP_ERROR_KEY, NAME_GROUP_ERROR_KEY } from '../typings'
import type { UpdateRequiredFieldsErrors } from '../typings'
import { groupMissingFields } from './groupMissingFields'

const REQUIRED = 'Required'

const ADDITIONAL_ERROR_KEYS = {
  divisionId: ['divisionId'],
  altPhoneNumber: ['altPhoneNumber'],
  address: ['street1', 'city', 'stateAbbreviation', 'postalCode'],
  locationTypeId: ['locationTypeId'],
  note: ['note'],
  targetPests: ['targetPestIds'],
  campaignId: ['campaignId'],
  leadSourceId: ['sourceId'],
  referral: ['referralCode'],
} as const

/**
 * Maps the fields a `{ converted: false }` response still reports missing back onto the modal's
 * error keys with `Required`, using the same keys as validateUpdateRequiredFields (name and
 * phone/email use their group keys). Custom field errors need `customFields` to know each
 * field's type; ones that cannot be placed are skipped.
 */
export function mapMissingFieldsToErrors(
  missing: IAutoReviewMissingField[],
  customFields: CustomField[] = [],
): UpdateRequiredFieldsErrors {
  const errors: UpdateRequiredFieldsErrors = {}
  const set = (key: string) => {
    ;(errors as Record<string, unknown>)[key] = REQUIRED
  }

  groupMissingFields(missing).forEach((group) => {
    switch (group.kind) {
      case 'nameOrCompany':
        errors[NAME_GROUP_ERROR_KEY] = REQUIRED
        break
      case 'phoneOrEmail':
        errors[CONTACT_GROUP_ERROR_KEY] = REQUIRED
        break
      case 'branchId':
        set('branchId')
        break
      case 'assignment':
        group.fields.forEach((f) => f.fieldKey && set(f.fieldKey))
        break
      case 'additional':
        ADDITIONAL_ERROR_KEYS[group.key].forEach(set)
        break
      case 'custom': {
        const field = customFields.find((f) => f.id === group.fieldId)
        if (field) {
          const type = customFieldTypeToFormType(field.type)
          errors.customFields = {
            ...errors.customFields,
            [type]: {
              ...errors.customFields?.[type],
              [group.fieldId]: REQUIRED,
            },
          } as UpdateRequiredFieldsErrors['customFields']
        }
        break
      }
      default:
        break
    }
  })

  return errors
}
